import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { ProductPrice } from "@/components/ProductPrice";
import { resolveImgUrl } from "@/components/ImageUploader";
import { useSizeAgeMap } from "@/lib/useSizeAgeMap";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, items, subtotal, updateQty, removeItem } = useCart();
  const navigate = useNavigate();
  const sizeLabel = useSizeAgeMap();
  const shipping = 0;
  const total = subtotal;

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <SheetTitle className="font-display text-2xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-pink-500"/> Your Cart
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto rounded-full bg-pink-50 flex items-center justify-center text-3xl mb-4">🛍️</div>
              <p className="font-display text-lg text-slate-700">Your cart is empty</p>
              <p className="text-sm text-slate-500 mt-1">Add some adorable outfits!</p>
              <Button onClick={() => { setDrawerOpen(false); navigate("/shop"); }} className="mt-6 rounded-full brand-gradient-pink text-white border-0">Browse Collection</Button>
            </div>
          ) : items.map((it) => {
            // Prefer the chosen color's first image, fall back to product top-level
            let lineImg = it.product?.images?.[0];
            let maxStock = it.product?.stock ?? 0;
            if (it.color && it.product?.colors?.length) {
              const cv = it.product.colors.find((c) => c.name === it.color);
              if (cv?.images?.[0]) lineImg = cv.images[0];
              if (cv) maxStock = cv.stock ?? 0;
            }
            return (
            <div key={`${it.product_id}_${it.size}_${it.color || ""}`} className="flex gap-3 p-3 rounded-2xl bg-slate-50/70 hover:bg-slate-50 transition-colors" data-testid={`cart-item-${it.product_id}`}>
              <img src={resolveImgUrl(lineImg)} alt="" className="w-20 h-24 rounded-xl object-cover bg-white"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-slate-900 line-clamp-2">{it.product?.name}
                  {it.product?.item_code && (
                    <span className="text-[10px] uppercase text-slate-400 ml-1">{it.product.item_code}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 text-xs text-slate-500 mt-0.5">
                  {it.size && <span data-testid={`cart-item-size-${it.product_id}`}>Size: {sizeLabel(it.size)}</span>}
                  {it.color && it.color !== "Standard" && <span data-testid={`cart-item-color-${it.product_id}`}>Color: {it.color}</span>}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full">
                    <button onClick={() => updateQty(it.product_id, it.size, it.color, Math.max(0, it.qty - 1))} data-testid={`line-minus-${it.product_id}-${it.size}-${it.color || ""}`} className="w-7 h-7 flex items-center justify-center hover:text-pink-500"><Minus className="w-3 h-3"/></button>
                    <span className="w-6 text-center text-xs font-bold">{it.qty}</span>
                    <button onClick={() => updateQty(it.product_id, it.size, it.color, Math.min(it.qty + 1, maxStock))} disabled={it.qty >= maxStock} data-testid={`line-plus-${it.product_id}-${it.size}-${it.color || ""}`} className="w-7 h-7 flex items-center justify-center hover:text-pink-500 disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-3 h-3"/></button>
                  </div>
                  {maxStock > 0 && maxStock <= 5 && (
                    <span className="text-[10px] text-orange-600">Only {maxStock} left</span>
                  )}
                  <ProductPrice
                    price={(it.product?.price || 0) * it.qty}
                    mrp={it.product?.mrp ? it.product.mrp * it.qty : undefined}
                    size="md"
                  />
                </div>
              </div>
              <button onClick={() => removeItem(it.product_id, it.size, it.color)} className="text-slate-400 hover:text-red-500 self-start" data-testid={`remove-item-${it.product_id}`}>
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="border-t p-6 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{formatINR(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Shipping</span><span>{shipping === 0 ? <span className="text-green-600 font-bold">FREE</span> : formatINR(shipping)}</span></div>
            <div className="flex justify-between font-display font-semibold text-lg pt-2 border-t"><span>Total</span><span>{formatINR(total)}</span></div>
            <Button onClick={() => { setDrawerOpen(false); navigate("/checkout"); }} data-testid="checkout-btn"
              className="w-full rounded-full brand-gradient-pink text-white border-0 h-12 btn-bouncy text-sm font-bold uppercase tracking-wider">
              Checkout · {formatINR(total)}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
